import type { InitiativeBlock } from "@nnf3d/studio/types";
import type { PortableTextMarkComponentProps } from "@portabletext/react";
import type { MouseEventHandler } from "react";

import { tooltipStore } from "../../lib/store";
import type { ParentComponent } from "../../lib/types";

type Block = Extract<NonNullable<InitiativeBlock[number]>, { _type: "block" }>;

type LinkType = NonNullable<Block["markDefs"]>[number];

let timeoutId: ReturnType<typeof setTimeout> | null = null;
const handleCopyValue: MouseEventHandler = (event): void => {
	if (!(event.currentTarget instanceof HTMLElement)) {
		return;
	}

	const { value, successMessage } = event.currentTarget.dataset;
	if (!value) {
		return;
	}

	if (timeoutId) {
		clearTimeout(timeoutId);
	}

	navigator.clipboard
		.writeText(value)
		.then(() => {
			tooltipStore.setState(successMessage ?? "Copied");
			timeoutId = setTimeout(() => {
				tooltipStore.setState(null);
			}, 1500);
			return null;
		})
		.catch(console.error);
};

const Link: ParentComponent<PortableTextMarkComponentProps<LinkType>> = ({ children, value }) => {
	if (value?.external) {
		return (
			<a
				href={value.external}
				target="_blank"
				className="cursor-pointer text-accent underline decoration-from-font underline-offset-2 visited:text-accent-visited hover:opacity-70 active:opacity-50"
			>
				{children}
				{" ↗"}
			</a>
		);
	}

	if (value?.mailto) {
		return (
			<button
				type="button"
				className="cursor-pointer text-accent underline decoration-from-font underline-offset-2 visited:text-accent-visited hover:opacity-70 active:opacity-50"
				data-value={value.mailto}
				onClick={handleCopyValue}
				data-success-message="Copied email"
			>
				{children}
			</button>
		);
	}

	if (value?.tel) {
		return (
			<button
				type="button"
				className="cursor-pointer text-accent underline decoration-from-font underline-offset-2 visited:text-accent-visited hover:opacity-70 active:opacity-50"
				data-value={value.tel}
				onClick={handleCopyValue}
				data-success-message="Copied phone"
			>
				{children}
			</button>
		);
	}

	return <span>{children}</span>;
};

export { Link };
